/**
 * External Service - Data from external APIs via adapters
 *
 * Supports:
 * - Read all (paginated)
 * - Read by ID
 * - LRU caching with TTL
 * - Reactive invalidation via events
 * - CRUD via adapter (if supported)
 *
 * Uses DatabaseAdapter from SDK (unified adapter interface).
 */

import type {
  ExternalEntityDefinition,
  PaginatedResult,
  ModuleContext,
  EntityServiceHooks,
  DatabaseAdapter,
  ManagedCache
} from '@gzl10/nexus-sdk'
import type { EntityQuery } from '@kernel/runtime/index.js'
import { BaseEntityService } from './base.service.js'
import { registerLiveObserver } from '../helpers/live-observer.js'

export class ExternalService<T extends Record<string, unknown> = Record<string, unknown>>
  extends BaseEntityService<T> {

  declare readonly definition: ExternalEntityDefinition
  private adapter: DatabaseAdapter | undefined
  private cache: ManagedCache<unknown>

  constructor(ctx: ModuleContext, definition: ExternalEntityDefinition, hooks?: EntityServiceHooks<T>, repo?: import('../repositories/types.js').EntityRepository) {
    super(ctx, definition, hooks, repo)

    // Get adapter from context registry (unified system)
    if (ctx.adapters.has(definition.adapter)) {
      this.adapter = ctx.adapters.get(definition.adapter)
    }

    // Shared cache across all external entities, with per-entity invalidation rules
    this.cache = ctx.core.cache.getOrCreate<unknown>('runtime:external', {
      maxEntries: 100,
      defaultTTL: definition.cache?.ttl ?? 60
    })

    if (definition.cache?.invalidateOn?.length) {
      this.cache.addInvalidationRules(definition.cache.invalidateOn)
    }

    // Register live mode observer: push updates when cache is invalidated
    if (definition.realtime === 'live' && definition.cache?.invalidateOn?.length) {
      registerLiveObserver({
        ctx, events: definition.cache.invalidateOn,
        moduleName: this.moduleName, entityKey: this.entityKey, label: definition.label
      })
    }
  }

  /**
   * Get table - the resource identifier in the external system
   */
  protected override get table(): string {
    return this.definition.table
  }

  /**
   * Get cache key for a request
   */
  private getCacheKey(operation: string, id?: string, query?: Record<string, unknown>): string {
    const keyField = this.definition.cache?.key ?? 'id'
    const base = `external:${this.definition.adapter}:${this.table}:${operation}`
    if (id) return `${base}:${keyField}:${id}`
    if (query && Object.keys(query).length > 0) return `${base}:${JSON.stringify(query)}`
    return base
  }

  /**
   * Ensure adapter is available
   */
  private ensureAdapter(): DatabaseAdapter {
    if (!this.adapter) {
      throw new this.errors.AppError(
        `Adapter '${this.definition.adapter}' not registered. Register it with ctx.registerAdapter().`,
        500
      )
    }
    return this.adapter
  }

  /**
   * Get all items with pagination
   */
  async findAll(query?: EntityQuery): Promise<PaginatedResult<T>> {
    const adapter = this.ensureAdapter()

    // Check cache (include query params to avoid stale results)
    const cacheKey = this.getCacheKey('findAll', undefined, query as unknown as Record<string, unknown>)
    const cached = await this.cache.get(cacheKey) as PaginatedResult<T> | null
    if (cached) {
      return cached
    }

    const result = await adapter.findMany<T>(this.table, query)

    // Cache result with entity-specific TTL
    const ttl = this.definition.cache?.ttl
    if (ttl && ttl > 0) {
      await this.cache.set(cacheKey, result, ttl)
    }

    return result
  }

  /**
   * Get item by ID
   */
  async findById(id: string): Promise<T | null> {
    const adapter = this.ensureAdapter()

    // Check cache
    const cacheKey = this.getCacheKey('findById', id)
    const cached = await this.cache.get(cacheKey) as T | null
    if (cached) {
      return cached
    }

    const result = await adapter.findById<T>(this.table, id)

    // Cache result with entity-specific TTL
    if (result) {
      const ttl = this.definition.cache?.ttl
      if (ttl && ttl > 0) {
        await this.cache.set(cacheKey, result, ttl)
      }
    }

    return this.processAfterFindById(result)
  }

  /**
   * Create via adapter
   */
  async create(data: Partial<T>): Promise<T> {
    const adapter = this.ensureAdapter()

    const processedData = await this.beforeCreate(data)
    const result = await adapter.insert<T>(this.table, processedData as Record<string, unknown>)
    await this.afterCreate(result)

    // Invalidate list cache
    await this.cache.delete(this.getCacheKey('findAll'))

    return result
  }

  /**
   * Update via adapter
   */
  async update(id: string, data: Partial<T>): Promise<T> {
    const adapter = this.ensureAdapter()

    const processedData = await this.beforeUpdate(id, data)
    const result = await adapter.update<T>(this.table, id, processedData as Record<string, unknown>)
    await this.afterUpdate(result)

    // Invalidate caches
    await this.cache.delete(this.getCacheKey('findAll'))
    await this.cache.delete(this.getCacheKey('findById', id))

    return result
  }

  /**
   * Delete via adapter
   */
  async delete(id: string): Promise<void> {
    const adapter = this.ensureAdapter()

    await this.beforeDelete(id)
    await adapter.delete(this.table, id)
    await this.afterDelete(id)

    // Invalidate caches
    await this.cache.delete(this.getCacheKey('findAll'))
    await this.cache.delete(this.getCacheKey('findById', id))
  }

  /**
   * Clear all cache for this entity
   */
  async clearCache(): Promise<void> {
    const prefix = `external:${this.definition.adapter}:${this.table}:`
    await this.cache.deleteByPrefix(prefix)
  }

  /**
   * Refresh data from external source (bypass cache)
   */
  async refresh(query?: EntityQuery): Promise<PaginatedResult<T>> {
    await this.clearCache()
    return this.findAll(query)
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return this.cache.getStats()
  }
}
